import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';

// Add Mapbox types if not already globally declared elsewhere
declare global {
  interface Window {
    mapboxgl?: any;
  }
}

interface MapPreviewProps {
  name: string;
  latitude?: number;
  longitude?: number;
}

// Simple Map Preview Component using Mapbox
const MapPreview: React.FC<MapPreviewProps> = ({
  name,
  latitude,
  longitude
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);

  // Default to a central location if coordinates are not provided
  const lat = latitude || 0;
  const lng = longitude || 0;
  const hasValidCoordinates = latitude !== undefined && longitude !== undefined && latitude !== 0 && longitude !== 0;

  useEffect(() => {
    // Skip if no valid coordinates or map element
    if (!hasValidCoordinates || !mapRef.current) {
      setMapLoaded(false); // Ensure loading state is false if no coords
      return;
    }

    let isMounted = true; // Flag to prevent updates on unmounted component

    // Load Mapbox script if not already loaded
    const loadMapbox = async () => {
      try {
        // Check if Mapbox is already loaded
        if (window.mapboxgl) {
          if(isMounted) initializeMap();
          return;
        }

        // If not already loaded, we'll use a placeholder instead
        // TODO: Implement dynamic script loading if needed
        console.warn("Mapbox GL JS not loaded. Displaying placeholder.");
        if(isMounted) setMapLoaded(false);
      } catch (error) {
        console.error("Error loading map:", error);
        if(isMounted) setMapLoaded(false);
      }
    };

    // Initialize map once Mapbox is loaded
    const initializeMap = () => {
      if (!window.mapboxgl || !mapRef.current || !isMounted) return;

      // Use Mapbox token from environment variables - ensure VITE_MAPBOX_TOKEN is set
      const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;
      if (!mapboxToken) {
          console.error("Mapbox token (VITE_MAPBOX_TOKEN) is not configured!");
          if(isMounted) setMapLoaded(false);
          return;
      }
      window.mapboxgl.accessToken = mapboxToken;

      // Create new map instance
      map.current = new window.mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/dark-v10', // Dark theme
        center: [lng, lat],
        zoom: 13,
        interactive: false // Disable interactivity for preview
      });

      // Add marker when map loads
      map.current.on('load', () => {
          if (!isMounted) return; // Check mount status again inside async callback
        // Add marker for the hotel
        new window.mapboxgl.Marker({
          color: '#10b981', // Emerald color to match theme
          scale: 0.8
        })
          .setLngLat([lng, lat])
          .addTo(map.current);

        setMapLoaded(true);
      });

      map.current.on('error', (e: any) => {
          console.error('Mapbox error:', e);
           if(isMounted) setMapLoaded(false); // Handle map errors
      });
    };

    loadMapbox();

    // Cleanup function
    return () => {
      isMounted = false;
      if (map.current) {
        try {
            map.current.remove();
        } catch (e) {
            console.error("Error removing map:", e);
        }
        map.current = null;
      }
    };
  }, [lat, lng, hasValidCoordinates, name]);

  if (!hasValidCoordinates) {
    return (
      <div className="bg-gray-800/50 rounded-md border border-white/10 p-3 flex flex-col items-center justify-center min-h-[120px] text-center">
        <MapPin className="text-gray-500 mb-2 w-6 h-6" />
        <p className="text-[10px] text-gray-500">Location coordinates not available</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-md border border-white/10 h-[120px] bg-gray-900"> {/* Added bg color */}
      {!mapLoaded && (
        <div className="absolute inset-0 bg-gray-800/70 flex items-center justify-center z-10">
          <div className="text-white/50 text-xs animate-pulse">Loading map...</div>
        </div>
      )}
      <div
        ref={mapRef}
        className="w-full h-full"
        aria-label={`Map showing location of ${name}`} // Add accessibility label
      />
      {mapLoaded && ( // Only show overlay if map loaded
         <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 z-20 pointer-events-none">
           <div className="flex items-center">
             <MapPin className="w-3 h-3 text-emerald-400 mr-1 flex-shrink-0" />
             <span className="text-white/90 text-[10px] truncate">{name}</span>
           </div>
         </div>
      )}
    </div>
  );
};

export default MapPreview; 